import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, ClipboardList, Calendar, Building, ArrowRight, AlertCircle, X, ChevronRight, Search, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { useSurvey } from '../context/surveycontext';
import api from '../apiConfig/api';
import { UnauthorizedSurveyTable } from '../components/unauthorizedtable';
import AssetDetailsSection from '../components/assetdetails';
import { OrgSummaryTable } from '../components/org_summary';
import WelcomeHeader from '../components/welcomeheader';
import StatsCards from '../components/statscard';
import SurveyBarChart from '../components/surveychart';

// Utility function for Indian number formatting
const formatIndianNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return parseInt(num).toLocaleString('en-IN');
};

const LoadingText = ({ width = "w-40", height = "h-6" }) => (
  <div className={`inline-block ${width} ${height} animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded relative overflow-hidden`}>
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
  </div>
);

const LoadingValue = () => (
  <span className="animate-pulse">
    <span className="h-8 w-16 bg-gray-200 rounded inline-block"></span>
  </span>
);

const ErrorMessage = ({ message }) => (
  <div className="flex items-center gap-2 text-orange-600 bg-orange-50 p-3 rounded-lg">
    <AlertCircle className="h-5 w-5" />
    <span>{message}</span>
  </div>
);

const LoadingItem = () => (
  <div className="grid grid-cols-5 gap-4 py-3">
    <div className="col-span-2 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-32"></div>
      <div className="h-3 bg-gray-200 rounded w-24"></div>
    </div>
    <div className="flex justify-center">
      <div className="h-4 bg-gray-200 rounded w-12"></div>
    </div>
    <div className="flex justify-center">
      <div className="h-4 bg-gray-200 rounded w-12"></div>
    </div>
    <div className="flex justify-center">
      <div className="h-4 bg-gray-200 rounded w-12"></div>
    </div>
  </div>
);

const CompletedSurveyHeader = () => (
  <div className="hidden lg:grid grid-cols-5 gap-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg px-4">
    <div className="col-span-2">
      <h4 className="text-sm font-medium text-gray-600">Ward Details</h4>
    </div>
    <div className="text-center">
      <h4 className="text-sm font-medium text-gray-600">Completed</h4>
    </div>
    <div className="text-center">
      <h4 className="text-sm font-medium text-gray-600">Pending</h4>
    </div>
    <div className="text-center">
      <h4 className="text-sm font-medium text-gray-600">Total</h4>
    </div>
  </div>
);

const CompletedSurveyItem = ({ report, onCompletedClick }) => (
  <div className="flex flex-col lg:grid lg:grid-cols-5 gap-4 py-3 border-b border-gray-100 last:border-0 px-4 hover:bg-gray-50">
    <div className="col-span-2">
      <h3 className="font-medium text-gray-800">{report.ward_name}</h3>
      <div className="text-xs text-gray-500 mt-1">
        <p>{report.org_name}</p>
        <p>{report.zone_name}</p>
      </div>
    </div>
    <div className="flex justify-between lg:justify-center items-center">
      <span className="text-sm text-gray-500 lg:hidden">Completed:</span>
      <button
        onClick={() => onCompletedClick(report)}
        className="font-medium text-green-600 hover:text-green-700 transition-colors hover:underline"
      >
        {formatIndianNumber(report.completed)}
      </button>
    </div>
    <div className="flex justify-between lg:justify-center items-center">
      <span className="text-sm text-gray-500 lg:hidden">Pending:</span>
      <span className="font-medium text-blue-600">{formatIndianNumber(report.pending)}</span>
    </div>
    <div className="flex justify-between lg:justify-center items-center">
      <span className="text-sm text-gray-500 lg:hidden">Total:</span>
      <span className="font-medium text-gray-700">{formatIndianNumber(report.total)}</span>
    </div>
  </div>
);

const AssetDetailsModal = ({ isOpen, onClose, assetDetails, isLoading, wardInfo, onAssetClick }) => {
  // Add state for search query
  const [searchQuery, setSearchQuery] = useState('');
  
  if (!isOpen) return null;
  
  // Filter assets based on search query
  const filteredAssets = searchQuery.trim() === '' 
    ? assetDetails 
    : assetDetails.filter(asset => {
        const query = searchQuery.toLowerCase();
        return (
          (asset.street_name && asset.street_name.toLowerCase().includes(query)) ||
          (asset.owner && asset.owner.toLowerCase().includes(query)) ||
          (asset.usage && asset.usage.toLowerCase().includes(query))
        );
      });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-800">
            Asset Details - {wardInfo?.ward_name || 'N/A'} - Surveys Completed
          </h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        {/* Add search input below the header */}
        <div className="px-4 py-3 border-b border-gray-200 sticky top-16 bg-white">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search by street name, owner or usage..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <div className="overflow-auto p-4">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : filteredAssets.length > 0 ? (
            <div className="grid gap-4">
              {filteredAssets.map((asset, index) => (
                <div
                  key={index}
                  onClick={() => onAssetClick(asset.id)}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-lg hover:border-gray-300 transition-all cursor-pointer"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Street Name</p>
                      <p className="font-medium">{asset.street_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Owner Name</p>
                      <p className="font-medium">{asset.owner || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Usage</p>
                      <p className={`font-medium ${
                        asset.usage?.toLowerCase() === 'commercial' || 
                        asset.usage?.toLowerCase() === 'mixed'
                          ? 'text-red-600'
                          : 'text-gray-800'
                      }`}>
                        {asset.usage || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-end text-sm text-blue-600">
                    View Details <ChevronRight className="h-4 w-4" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {searchQuery.trim() !== '' 
                ? "No matching assets found" 
                : "No asset details available"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { setIsSurveyEnabled, setSurveyParams } = useSurvey();
  const dataFetchedRef = useRef(false);
  const [userName, setUserName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [globalError, setGlobalError] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [assetReports, setAssetReports] = useState([]);
  const [surveyDate, setSurveyDate] = useState('');
  const [userData, setUserData] = useState({
    designation: '',
    organizationName: '',
    userId: '',
    orgId: '',
    zoneName: '',
    zoneId: '',
    orgCode: ''
  });
  const [surveyStats, setSurveyStats] = useState({
    totalCompleted: 0,
    totalPending: 0,
    monthlyCount: 0
  });
  const [wardData, setWardData] = useState([]);
  const [dailySurveyData, setDailySurveyData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWard, setSelectedWard] = useState(null);
  const [assetDetails, setAssetDetails] = useState([]);
  const [isLoadingAssetDetails, setIsLoadingAssetDetails] = useState(false);

  const getHomeData = async () => {
    try {
      const homeDataResponse = await api.get('/user/homedata');
      
      if (homeDataResponse?.data?.user) {
        const user = homeDataResponse.data.user;
        
        // Create header info using the API structure
        const headerInfo = {
          name: user.Name || '',
          role: user.DesignationName || 'No designation',
          designation: user.DesignationName || 'No designation',
          organizationName: user.ULBDetails?.[0]?.OrgName || 'Organization'
        };
        localStorage.setItem('headerInfo', JSON.stringify(headerInfo));
        
        // Set user name from the structure
        setUserName(user.Name || '');
        setSurveyDate(homeDataResponse?.data?.data?.[0]?.created || new Date().toISOString());
        
        // Save the data we'd normally get from proapi directly
        const orgCode = user.ULBDetails?.[0]?.OrgCode;
        const orgName = user.ULBDetails?.[0]?.OrgName;
        const orgId = user.ULBDetails?.[0]?.OrgId;
        
        localStorage.setItem('org_id', orgId || '');
        localStorage.setItem('organization_name', orgName || '');
        localStorage.setItem('organization_code', orgCode || '');
        localStorage.setItem('user_id', user.UserID || '');
        
        // Update userData state
        setUserData(prevData => ({
          ...prevData,
          userId: user.UserID || '',
          orgId: orgId || '',
          orgCode: orgCode || '',
          organizationName: orgName || '',
          designation: user.DesignationName || ''
        }));
        
        setUserRole(user.DesignationName || 'No designation');
        
        return {
          userData: user,
          orgCode: orgCode,
          organizationSummary: Array.isArray(homeDataResponse?.data?.data) ? homeDataResponse.data.data : []
        };
      }
    } catch (error) {
      console.error('Home Data API Error:', error);
      
      // Fall back to localStorage data if the API call fails
      try {
        const storedHomeData = localStorage.getItem('homeData');
        
        if (storedHomeData) {
          const parsedHomeData = JSON.parse(storedHomeData);
          const user = parsedHomeData.user;
          
          if (user) {
            // Create header info from stored data
            const headerInfo = {
              name: user.Name || '',
              role: user.DesignationName || 'No designation',
              designation: user.DesignationName || 'No designation',
              organizationName: user.ULBDetails?.[0]?.OrgName || 'Organization'
            };
            localStorage.setItem('headerInfo', JSON.stringify(headerInfo));
            
            // Set user name from stored data
            setUserName(user.Name || '');
            setSurveyDate(parsedHomeData?.data?.[0]?.created || new Date().toISOString());
            
            // Get organization data from stored user data
            const orgCode = user.ULBDetails?.[0]?.OrgCode;
            const orgName = user.ULBDetails?.[0]?.OrgName;
            const orgId = user.ULBDetails?.[0]?.OrgId;
            
            // Update userData state with stored values
            setUserData(prevData => ({
              ...prevData,
              userId: user.UserID || '',
              orgId: orgId || '',
              orgCode: orgCode || '',
              organizationName: orgName || '',
              designation: user.DesignationName || ''
            }));
            
            setUserRole(user.DesignationName || 'No designation');
            
            return {
              userData: user,
              orgCode: orgCode,
              organizationSummary: Array.isArray(parsedHomeData?.data) ? parsedHomeData.data : []
            };
          }
        }
      } catch (fallbackError) {
        console.error('Failed to use fallback home data:', fallbackError);
      }
      
      setGlobalError('Failed to load user data');
    }
    return null;
  };

  const handleAssetClick = (assetId) => {
    if (!assetId) return;
    navigate(`/report/${assetId}`);
  };

  const handleCompletedClick = async (wardInfo) => {
    try {
      setIsLoadingAssetDetails(true);
      setSelectedWard(wardInfo);
      setIsModalOpen(true);
  
      // Get organization_code from localStorage instead of using org_name
      const orgCode = localStorage.getItem('organization_code');
  
const params = new URLSearchParams({
  org: orgCode, // Field name is 'org', value is orgCode from localStorage
  zone: wardInfo.zone_name,
  ward: wardInfo.ward_name
});
  
      const response = await api.get(`/asset/report?${params}`);
      setAssetDetails(response.data.data || []);
    } catch (error) {
      console.error('Error fetching asset details:', error);
      setAssetDetails([]);
    } finally {
      setIsLoadingAssetDetails(false);
    }
  };

  const fetchData = async () => {
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;
    
    try {
      setIsLoading(true);
      setGlobalError(null);

      const homeDataResult = await getHomeData();
      if (!homeDataResult) {
        setIsLoading(false);
        return;
      }

      const { userData: user, organizationSummary } = homeDataResult;
      const normalizedRole = String(user.DesignationName || '').trim().toUpperCase();

      // CMA dashboard is selected by the role returned from /user/homedata.
      if (normalizedRole === "CMA") {
        await handleCMAFlow(user, organizationSummary);
      } else {
        await handleRegularFlow(user);
      }
    } catch (error) {
      console.error('Error in fetchData:', error);
      setGlobalError(error.message);
      
      // Set default empty data
      setAssetReports([{
        ward_name: 'No Data Available',
        completed: 0,
        pending: 0,
        total: 0,
        org_name: userData.organizationName || 'Organization',
        zone_name: 'N/A'
      }]);
      
      setSurveyStats({
        totalCompleted: 0,
        totalPending: 0,
        monthlyCount: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCMAFlow = async (user, organizationSummary = []) => {
    localStorage.setItem('org_code', 'CMA');
    localStorage.setItem('organization_name', 'COMMISSIONERATE OF MUNICIPAL ADMINISTRATION');
    localStorage.setItem('user_id', user.UserID || '');
    localStorage.setItem('org_id', user.ULBDetails?.[0]?.OrgId || '');

    const orgData = Array.isArray(organizationSummary) ? organizationSummary : [];
    setAssetReports(orgData);
    
    // Calculate stats for CMA - fixing the pending calculation
    const totals = orgData.reduce((acc, org) => {
      const completed = parseInt(org.completed) || 0;
      const total = parseInt(org.total) || 0;
      const pending = total - completed; // Calculate pending as total - completed
      
      return {
        totalCompleted: acc.totalCompleted + completed,
        totalPending: acc.totalPending + pending,
        totalAssets: acc.totalAssets + total
      };
    }, { totalCompleted: 0, totalPending: 0, totalAssets: 0 });

    setSurveyStats({
      totalCompleted: totals.totalCompleted,
      totalPending: totals.totalPending,
      monthlyCount: totals.totalCompleted,
      totalAssets: totals.totalAssets
    });

    const designation = user.DesignationName || user.Designation || user?.ULBDetails?.[0]?.Designation || '';

    setUserData(prevData => ({
      ...prevData,
      userId: user.UserID || '',
      orgId: user.ULBDetails?.[0]?.OrgId || '',
      orgCode: "CMA",
      organizationName: "COMMISSIONERATE OF MUNICIPAL ADMINISTRATION",
      designation: designation
    }));
  };

  const handleRegularFlow = async (user) => {
    const orgName = user.ULBDetails?.[0]?.OrgName;
    const orgCode = user.ULBDetails?.[0]?.OrgCode;
    
    // Check if zone data is available in user object
    if (user.zone && user.zone.length > 0) {
      localStorage.setItem('zone_name', user.zone[0].ZoneName || '');
      
      setUserData(prevData => ({
        ...prevData,
        zoneName: user.zone[0].ZoneName || '',
      }));
    }
    
    // Check if ward data exists in user object
    if (user.ward && user.ward.length > 0) {
      const wardDetails = user.ward.map(ward => ({
        wardId: ward.WardID,
        wardName: ward.WardName
      }));
      localStorage.setItem('ward_details', JSON.stringify(wardDetails));
      setWardData(user.ward);
      
      // Get ward summary data
      const wardIds = user.ward.map(ward => ward.WardID).join(',');
      const wardSummaryResponse = await api.get(`/user/asst_smry?ward_ids=${wardIds}`);
      const wardSummaryData = wardSummaryResponse?.data?.data || [];

      setAssetReports(wardSummaryData);
      const totals = wardSummaryData.reduce((acc, ward) => ({
        totalCompleted: acc.totalCompleted + (parseInt(ward.completed) || 0),
        totalPending: acc.totalPending + (parseInt(ward.pending) || 0)
      }), { totalCompleted: 0, totalPending: 0 });

      setSurveyStats({
        totalCompleted: totals.totalCompleted,
        totalPending: totals.totalPending,
        monthlyCount: totals.totalCompleted
      });

      setSurveyParams({
        wardData: user.ward,
        orgId: user.ULBDetails?.[0]?.OrgId,
        userId: user.UserID,
        organizationName: user.ULBDetails?.[0]?.OrgName
      });
      setIsSurveyEnabled(true);
    } else {
      // No ward data, get summary by organization
      if (orgName) {
    const summaryResponse = await api.get(`/user/asst_smry?org=${encodeURIComponent(orgCode)}`);
        const summaryData = summaryResponse?.data?.data || [];
        setAssetReports(summaryData);
      }
    }
  };

  useEffect(() => {
    fetchData();
    return () => {
      dataFetchedRef.current = false;
    };
  }, []);

  const handleStartSurvey = () => {
    navigate('/filters', { 
      state: { 
        wardData,             
        orgId: userData.orgId,        
        userId: userData.userId,      
        organizationName: userData.organizationName,
        zoneName: userData.zoneName,
        orgCode: userData.orgCode 
      }
    });
  };

  const handleRefresh = async () => {
    dataFetchedRef.current = false;
    await fetchData();
  };

  const renderUI = () => {
    if (userData.orgCode === "CMA") {
      return (
        <>
          <div className="mb-6 flex items-center justify-between px-1 py-2">
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Assessment Survey on EB/GST Data</h1>
            <button type="button" onClick={handleRefresh} disabled={isLoading} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          <StatsCards isLoading={isLoading} surveyStats={surveyStats} />
          <OrgSummaryTable 
            data={assetReports} 
            isLoading={isLoading} 
            onNavigateToReport={handleAssetClick}
          />
        </>
      );
    }
  
    if (wardData?.length > 0) {
      return (
        <>
          <WelcomeHeader 
            userName={userName}
            isLoading={isLoading}
            userData={userData}
            wardData={wardData}
            handleStartSurvey={handleStartSurvey}
          />
          <StatsCards isLoading={isLoading} surveyStats={surveyStats} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <AssetDetailsSection 
  isLoading={isLoading}
  assetReports={assetReports}
  onCompletedClick={handleCompletedClick}
  onNavigateToReport={handleAssetClick}
/>
            <SurveyBarChart
              isLoading={isLoading}
              dailySurveyData={dailySurveyData}
              assetReports={assetReports}
            />
          </div>
        </>
      );
    }
    
    return (
      <>
        <WelcomeHeader 
          userName={userName}
          isLoading={isLoading}
          userData={userData}
          wardData={wardData}
          handleStartSurvey={handleStartSurvey}
        />
   <UnauthorizedSurveyTable 
  isLoading={isLoading}
  assetReports={assetReports}
  onCompletedClick={handleCompletedClick}
  onNavigateToReport={handleAssetClick}  // Add this line
/>
      </>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {globalError && (
          <div className="mb-4">
            <ErrorMessage message={globalError} />
          </div>
        )}
        {renderUI()}
        <AssetDetailsModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          assetDetails={assetDetails}
          isLoading={isLoadingAssetDetails}
          wardInfo={selectedWard}
          onAssetClick={handleAssetClick}
        />
      </div>
    </div>
  );
};

export default HomePage;
